/* oxlint-disable max-lines -- legacy file, split it before you add code */
import { AnchorButton, Button } from '@askable/ui/core/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@askable/ui/core/dropdown-menu';
import { toast } from '@askable/ui/core/sonner';
import { cn } from '@askable/ui/lib/utils';
import { useFeatureFlags } from 'feature-flags';
import find from 'lodash/find';
import get from 'lodash/get';
import size from 'lodash/size';
import { CircleAlert, ChevronDown, UserCircle } from 'lucide-react';
import momentTimezone from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'urql';

import { Dialog, LoadingOverlay } from 'components/common';
import { useConnectedClient } from 'context/ConnectedClientContext';
import { DeleteSession } from 'data/mutations/booking/deleteSession.mutation';
import { SearchAskableMeeting } from 'data/queries/askable_video/SearchAskableMeeting.query';
import { FetchParticipantSessionsSearch } from 'data/queries/bookingSubmission/fetchParticipantSessionsSearch.query';
import { bookingUtils } from 'lib/booking';
import { BOOKING_CONFIG_PARTICIPANT_AGREEMENT_TYPE, SUBMISSION_AGREEMENT_STATUS } from 'lib/constants';
import { getCombinedErrorMessage } from 'utils/error_utils';

import ActionsContainer from './actionsContainer';
import AssignSessionTo from './assignSessionTo';
import { isExpertNoviceSessionBooking } from './expertNoviceSession';
import { ExpertNoviceSessionDetails } from './ExpertNoviceSessionDetails';

import { SessionPagesPanel } from './sessionPages/SessionPagesPanel';

import './sessionDetailsPopover.scss';

function SessionDetailsPopover(props: any) {
  const [sessionStatusSelected, setSessionStatusSelected] = useState();
  const { details } = useConnectedClient();
  const [{ data: askableVideoData }] = useQuery({
    query: SearchAskableMeeting,
    variables: {
      input: {
        _booking_id: props.booking?._id || '',
        _session_id: props.sessionSelected?._id || '',
      },
    },
    pause: !props.sessionSelected?._id || !props.booking?._id || !bookingUtils.isAskableLive(props.booking),
  });

  const isFocusGroup = bookingUtils.isFocusGroup(props.booking);
  const { llm_lab_features } = useFeatureFlags(['llm_lab_features']);
  const isExpertNovice =
    isExpertNoviceSessionBooking(props.booking, llm_lab_features) && !bookingUtils.isInDraft(props.booking);

  const sessionStatus = props.sessionSelected
    ? bookingUtils.getSessionStatus(
        props.sessionSelected.CurrentStatus,
        props.sessionSelected,
        props.sessionSelected.participants,
        props.timezone,
      )
    : null;

  const waitlistedShouldQuery =
    get(sessionStatus, 'label') === 'Confirmed' ||
    get(sessionStatus, 'label') === 'Cancelled' ||
    get(sessionStatus, 'class') === 'invited';

  const [{ fetching: waitlistedLoading, data: waitlistedData }] = useQuery({
    query: FetchParticipantSessionsSearch,
    variables: {
      _booking_id: props.booking?._id,
      _session_id: [props.sessionSelected?._id],
      status: [2],
      cancel: [0],
    },
    pause: !props.booking?._id || !props.sessionSelected?._id || !waitlistedShouldQuery,
  });

  const focusGroupShouldQuery = isFocusGroup && (isExpertNovice || !!get(sessionStatus, 'label'));

  const [{ fetching: focusGroupLoading, data: focusGroupData }, refreshFocusGroup] = useQuery({
    query: FetchParticipantSessionsSearch,
    variables: {
      _booking_id: props.booking?._id,
      _session_id: [props.sessionSelected?._id],
      status: [1, 4],
      ...(isExpertNovice ? { cancel: [0] } : {}),
    },
    pause: !focusGroupShouldQuery || !props.booking?._id || !props.sessionSelected?._id,
  });

  useEffect(() => {
    if (isExpertNovice && props.sessionSelected?._id) refreshFocusGroup({ requestPolicy: 'network-only' });
  }, [isExpertNovice, props.sessionSelected, refreshFocusGroup]);

  const [displayCancelSessionDialog, setDisplayCancelSessionDialog] = useState(false);
  const [participantSelected, setParticipantSelected] = useState();
  const [openAssignSessionTo, setOpenAssignSessionTo] = useState(false);
  const [researcher, setResearcher] = useState({});

  useEffect(() => {
    const sessionResearchers = bookingUtils.getBookingResearchers(
      props.sessionTimes,
      details?._id!,
      `${details?.name?.firstname} ${details?.name?.lastname}`,
    );
    let researcherData;
    if (get(props, 'sessionSelected._researcher_user_id')) {
      researcherData = find(
        sessionResearchers,
        (item: any) => item._id.toString() === props.sessionSelected._researcher_user_id.toString(),
      );
    } else {
      researcherData = find(sessionResearchers, (item: any) => item._id.toString() === details?._id);
    }

    setResearcher(researcherData);
  }, [props]);

  const [, deleteSession] = useMutation(DeleteSession);

  if (!props.sessionSelected) {
    return null;
  }

  const { start, end, CurrentStatus, participants } = props.sessionSelected;
  const isHappeningOrInPast = momentTimezone().tz(props.timezone).isAfter(start);

  // Last time a participant took action to change his status
  const updatedTimestamp = get(CurrentStatus, `history[${get(sessionStatus, 'confirmation_field')}]`);
  const onDeleteSession = () => {
    // If should double check whether the client really wants to delete a session when a session is either Confirmed or Waitlisted
    if (get(sessionStatus, 'label') === 'Confirmed' || get(sessionStatus, 'label') === 'Waitlisted') {
      setDisplayCancelSessionDialog(true);
    } else {
      onConfirmDelete();
    }
  };

  const onConfirmDelete = () => {
    if (bookingUtils.isInDraft(props.booking)) {
      props.onDeleteSession();
      props.onClose();
      return;
    }
    // Main called props.onClose() synchronously right after firing the mutation,
    // so the popover closed instantly. Preserve that timing — surface the error
    // (if any) asynchronously via the .then branch.
    void deleteSession({
      booking_id: props.booking._id,
      session_id: get(props, 'sessionSelected._id'),
    }).then(result => {
      if (result.error) {
        toast.error(getCombinedErrorMessage(result.error, 'Error saving study'));
      }
    });
    props.onClose();
  };

  const renderWaitlistedParticipantsForSession = () => {
    // It should only render a specific area for waitlisted people if the session is confirmed
    if (
      get(sessionStatus, 'label') !== 'Confirmed' &&
      get(sessionStatus, 'label') !== 'Cancelled' &&
      get(sessionStatus, 'class') !== 'invited'
    ) {
      return null;
    }

    if (waitlistedLoading) return <LoadingOverlay style={{ opacity: 0.8 }} />;
    if (size(waitlistedData?.participantSessionsSearch) === 0) return null;

    return (
      <div className="waitlistedSessionStatus">
        {waitlistedData?.participantSessionsSearch?.map(renderWaitlistedParticipantsContent)}
      </div>
    );
  };

  const renderWaitlistedParticipantsContent = (participant: any) => {
    return (
      <div>
        <div key={participant._id} className="waitlistedSession flex-between flex gap-2">
          <div className="flex w-full gap-2">
            <UserCircle className="mt-1 h-4 w-4" height="20" width="20" />
            <div className="font-semibold">
              (Waitlisted) {get(participant, 'user.meta.identity.firstname')}{' '}
              {get(participant, 'user.meta.identity.lastname')}
            </div>
          </div>
          <div>{renderActionsContainer(participant)}</div>
        </div>
      </div>
    );
  };

  const renderSessionStatus = () => {
    // Available Session
    if (get(sessionStatus, 'label') === '') return null;

    if (!isFocusGroup) {
      return <div className="sessionStatus">{renderSessionStatusContent(CurrentStatus)}</div>;
    }
    // Use pre-fetched focus group data
    if (focusGroupLoading) return <LoadingOverlay style={{ opacity: 0.8 }} />;
    return (
      <div className="sessionStatus">{focusGroupData?.participantSessionsSearch?.map(renderSessionStatusContent)}</div>
    );
  };

  const renderActionsContainer = (participant: any) => {
    const participantSessionStatus = bookingUtils.getSessionStatus(
      participant,
      get(props, 'sessionSelected'),
      participants,
      props.timezone,
    );
    return (
      <DropdownMenu
        onOpenChange={(open: boolean) => {
          if (open) {
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ label: string; labelAction: st... Remove this comment to see the full error message
            setSessionStatusSelected(participantSessionStatus);
            setParticipantSelected(participant);
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="ghost">
            Actions <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <ActionsContainer
            booking={props.booking}
            sessionStatus={sessionStatusSelected}
            sessionSelected={get(props, 'sessionSelected')}
            participantSelected={participantSelected}
            timezone={props.timezone}
            context={props.context}
            onSendMessage={props.onSendMessage}
            confirmedParticipants={props.confirmedParticipants}
            onClose={() => {
              // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
              setSessionStatusSelected();
              props.onClose();
            }}
            viewParticipantsScreen={props.viewParticipantsScreen}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderRecordingLink = (sessionSelected: any) => {
    // If session is not confirmed, return null
    if (get(sessionSelected, 'CurrentStatus.status') !== 1 || get(sessionSelected, 'CurrentStatus.cancel') !== 0) {
      return null;
    }

    const meetingCode = askableVideoData?.searchAskableMeeting?.[0]?.code;
    const roomId = askableVideoData?.searchAskableMeeting?.[0]?.rooms?.[0]?._id;
    if (meetingCode && roomId) {
      return (
        <AnchorButton
          href={`/askable-sessions/${meetingCode}/playback/${roomId}?teamId=${props.booking?._team_id}`}
          target="_blank"
        >
          <img src="/icons/SessionsIcon.svg" alt="View recording" className="h-4 w-4" />
          View session
        </AnchorButton>
      );
    }
    return null;
  };

  const renderSessionStatusContent = (participant: any) => {
    const participantSessionStatus = bookingUtils.getSessionStatus(
      participant,
      get(props, 'sessionSelected'),
      participants,
      props.timezone,
    );
    const participantFeedback = get(participant, 'feedback.participant', '');
    const hasRating = get(participant, 'rating.engagement') || get(participant, 'rating.overall');

    let avgRating = 0;
    if (hasRating) {
      const { rating } = participant;
      avgRating = get(rating, 'overall');
      if (get(participant, 'rating.engagement')) {
        avgRating = (get(rating, 'engagement') + get(rating, 'punctuality')) / 2;
      }
    }

    return (
      <>
        <div
          key={participant._id}
          className={`sessionStatusInternal flex-between flex gap-2 status-${get(participantSessionStatus, 'class')}`}
        >
          <div className="flex w-full gap-2">
            <UserCircle className="mt-1 h-4 w-4" />

            <div className="personContextInternalContainer flex flex-col gap-1">
              <div className="font-semibold">
                {get(participant, 'user.meta.identity.firstname')} {get(participant, 'user.meta.identity.lastname')}
              </div>
              <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                {updatedTimestamp && (
                  <p className="updatedTime">
                    {get(participantSessionStatus, 'label')}{' '}
                    {momentTimezone(updatedTimestamp).tz(props.timezone).fromNow()}
                  </p>
                )}

                <div
                  className={cn('font-semibold', {
                    'text-warning': get(participantSessionStatus, 'class') === 'invited',
                    'text-success': get(participantSessionStatus, 'class') === 'confirmed',
                    'text-foreground': get(participantSessionStatus, 'class') === 'completed',
                    'text-destructive': get(participantSessionStatus, 'class') === 'cancelled',
                  })}
                >
                  {get(participantSessionStatus, 'labelAction')}
                </div>

                {renderSessionStatusWarnings(participant)}

                {bookingUtils.isRemote(props.booking) && bookingUtils.isAskableLive(props.booking) && (
                  <div className="pt-2">{renderRecordingLink(get(props, 'sessionSelected'))}</div>
                )}
              </div>
            </div>
          </div>
          <div>{renderActionsContainer(participant)}</div>
        </div>
        {hasRating && (
          <div className="clientFeedback">
            <img alt="clientFeedbackStar" src="/icons/fullStartIcon.svg" className="clientFeedbackStar" />
            <span className="clientFeedbackRating">{`You rated ${avgRating} ${avgRating > 1 ? 'stars' : 'star'}`}</span>
          </div>
        )}
        {participantFeedback && (
          <>
            <div className="participantFeedbackContainer">
              <img
                alt="participantFeedbackIcon"
                src="/icons/participantFeedbackIcon.svg"
                className="participantFeedbackIcon"
              />
              <span className="participantFeedbackTitle">Participant feedback:</span>
            </div>
            <div
              className={`participantFeedback ${get(props, 'booking.config.session.type') === 2 ? 'focusGroup' : ''}`}
            >{`"${participantFeedback}"`}</div>
          </>
        )}
      </>
    );
  };

  const renderSessionStatusWarnings = (participant: any) => {
    if (
      get(props, 'booking.config.participant_agreement.type') ===
        BOOKING_CONFIG_PARTICIPANT_AGREEMENT_TYPE.CUSTOM_AGREEMENT &&
      get(participant, 'status') === 1 &&
      get(participant, 'cancel') === 0 &&
      get(participant, 'Submission.agreement.status') !== SUBMISSION_AGREEMENT_STATUS.COMPLETED
    ) {
      return (
        <div className="status flex items-center gap-1 text-warning">
          <CircleAlert className="h-4 w-4" /> Agreement not signed
        </div>
      );
    }
  };

  const renderActionContainer = (
    itemId: string,
    iconPath: string,
    label: string,
    action: () => void,
    rightText?: string,
  ) => {
    return (
      <Button className="flex gap-2" onClick={action} id={itemId}>
        <img alt={`icon-${label}`} src={iconPath} className="h-4 w-4" />
        <span>{label}</span>

        {rightText && (
          <div className="rightText">
            <span>{rightText}</span>
          </div>
        )}
      </Button>
    );
  };

  const sessionStatusVisible = get(sessionStatus, 'label') !== '';

  return (
    <div>
      <div className="sessionDetailsComponentInternal w-full">
        <div className="flex-between align-center mb-4 flex gap-2">
          <div className="w-full">
            <div className="day">{momentTimezone(start).tz(props.timezone).format('dddd DD MMMM')}</div>
            <time className="font-semibold">
              {momentTimezone(start).tz(props.timezone).format('h:mm')} -{' '}
              {momentTimezone(end).tz(props.timezone).format('h:mma')}
            </time>
          </div>
          {researcher && (
            <div
              // @ts-expect-error ts-migrate(2339) FIXME: Property '_id' does not exist on type '{}'.
              key={researcher._id}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-bold text-background"
              // @ts-expect-error ts-migrate(2339) FIXME: Property 'color' does not exist on type '{}'.
              style={{ backgroundColor: researcher.color }}
            >
              {/* @ts-expect-error ts-migrate(2339) FIXME: Property 'name' does not exist on type '{}'. */}
              <div className="researcherInitials">{researcher.name}</div>
            </div>
          )}
        </div>
        {isExpertNovice && focusGroupLoading && <LoadingOverlay style={{ opacity: 0.8 }} />}
        {isExpertNovice && !focusGroupLoading && (
          <ExpertNoviceSessionDetails
            participants={focusGroupData?.participantSessionsSearch ?? []}
            renderParticipant={renderSessionStatusContent}
          />
        )}
        {!isExpertNovice && sessionStatusVisible && renderSessionStatus()}
        {(get(sessionStatus, 'label') === 'Confirmed' ||
          get(sessionStatus, 'label') === 'Cancelled' ||
          get(sessionStatus, 'class') === 'invited') &&
          renderWaitlistedParticipantsForSession()}
        {llm_lab_features &&
          bookingUtils.isAskableLive(props.booking) &&
          props.booking?._id &&
          props.sessionSelected?._id && (
            <SessionPagesPanel
              key={`${props.booking._id}:${props.sessionSelected._id}`}
              bookingId={props.booking._id}
              sessionId={props.sessionSelected._id}
            />
          )}
        {!isHappeningOrInPast && (
          <div className={cn('flex gap-2', sessionStatusVisible && 'ml-6 mt-2')}>
            <AssignSessionTo
              session={props.sessionSelected}
              open={openAssignSessionTo}
              booking={props.booking}
              context={props.context}
              onClose={() => setOpenAssignSessionTo(false)}
              onAssignSessionToResearcher={props.onAssignSessionToResearcher}
              onCloseDetails={props.onClose}
              onFinishAssignTo={(newSessionsData: any, newResearcherId: any) => {
                setOpenAssignSessionTo(false);
                const sessionResearchers = bookingUtils.getBookingResearchers(
                  newSessionsData.session,
                  details?.id!,
                  `${details?.name?.firstname} ${details?.name?.lastname}`,
                );
                const researcherData = find(
                  sessionResearchers,
                  (item: any) => item._id.toString() === newResearcherId.toString(),
                );

                setResearcher(researcherData);
              }}
            />
            {/* If the session is not available, we shouldnt give the option to delete the session */}
            {get(sessionStatus, 'label') === '' &&
              renderActionContainer(
                'deleteSessionAction',
                '/icons/deleteSession.svg',
                'Delete this session',
                onDeleteSession,
              )}
          </div>
        )}
      </div>

      <Dialog
        title="Are you sure you want to delete this session?"
        modal={false}
        open={displayCancelSessionDialog}
        onRequestClose={() => setDisplayCancelSessionDialog(false)}
        customActions={[
          {
            label: 'No',
            primary: true,
            customFunction: () => setDisplayCancelSessionDialog(false),
          },
          {
            label: 'Yes',
            buttonType: 'normal',
            containerButtonStyle: { marginLeft: '20px' },
            customFunction: () => {
              setDisplayCancelSessionDialog(false);
              onConfirmDelete();
            },
          },
        ]}
      >
        Note: {get(CurrentStatus, 'user.meta.identity.firstname')} will get notified
      </Dialog>
    </div>
  );
}

export default SessionDetailsPopover;
